/**
 * How a meal's date reads on a phone (SPEC §9.4).
 *
 * Both halves, always: "This Sunday · October 4 · 12:30 PM". A message link may
 * be opened days after it arrived, and "this Sunday" on its own is then a lie
 * the reader has no way of catching.
 */

/** A meal's date and time, split the way the screen draws it. */
export interface MealWhen {
  /** "Today", "Tomorrow", "This Sunday" — or null when it is too far off. */
  relative: string | null
  /** "October 4", or "October 4, 2027" when it is not this year. */
  absolute: string
  /** "12:30 PM". */
  time: string
}

/**
 * Parses the server's local date and time.
 *
 * `localDate` and `localTime` are already in the group's zone, so they are
 * assembled without an offset and read back as a local `Date`. Attaching `Z`
 * would shift a 12:30 lunch by however far the reader is from UTC.
 */
function parse(localDate: string, localTime: string): Date | null {
  const parsed = new Date(`${localDate}T${localTime}`)

  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/** Whole calendar days from one date to another, ignoring the time of day. */
function daysBetween(from: Date, to: Date): number {
  const a = new Date(from.getFullYear(), from.getMonth(), from.getDate())
  const b = new Date(to.getFullYear(), to.getMonth(), to.getDate())

  return Math.round((b.getTime() - a.getTime()) / 86_400_000)
}

/**
 * The relative half.
 *
 * Only ever said about a date close enough for it to help. "This Sunday" is
 * useful; "in eleven weeks" is a sentence nobody reads faster than the date
 * itself, so beyond a fortnight there is no relative half at all.
 *
 * Deliberately no "yesterday" or "last Sunday". A meal on the home screen is
 * the next one, and dressing a stale card in reassuring language is how
 * somebody turns up to a meal that already happened.
 */
function relativeTo(now: Date, when: Date): string | null {
  const days = daysBetween(now, when)

  if (days === 0) return 'Today'
  if (days === 1) return 'Tomorrow'
  if (days < 0 || days > 13) return null

  const weekday = when.toLocaleDateString(undefined, { weekday: 'long' })

  // Seven days out is the same weekday as today, which "this" would make
  // ambiguous with today itself. Everything in the coming week is "this".
  return days < 7 ? `This ${weekday}` : `Next ${weekday}`
}

/**
 * Describes a meal's date and time.
 *
 * @param localDate `yyyy-MM-dd` in the group's time zone.
 * @param localTime `HH:mm` in the group's time zone.
 * @param now The reader's clock, injected so the relative half is testable.
 */
export function describeWhen(
  localDate: string,
  localTime: string,
  now: Date = new Date(),
): MealWhen | null {
  const when = parse(localDate, localTime)

  if (!when) return null

  return {
    relative: relativeTo(now, when),
    absolute: when.toLocaleDateString(undefined, {
      month: 'long',
      day: 'numeric',
      // A meal in another year says so. Without it, "December 24" on a card
      // opened in January reads as this year's, which is eleven months out.
      ...(when.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
    }),
    time: when.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }),
  }
}

/** The whole thing on one line: "This Sunday · October 4 · 12:30 PM". */
export function whenLine(when: MealWhen | null): string {
  if (!when) return ''

  return [when.relative, when.absolute, when.time].filter(Boolean).join(' · ')
}

/**
 * The instant a calendar date begins in a given time zone, as ISO 8601 UTC.
 *
 * For filters that are picked as dates but queried as instants — the audit
 * trail, where "since 1 September" means the group's 1 September, not the
 * reader's (MT-110). No library: the zone's offset at that moment is read back
 * from `Intl`, and applied twice so a date on which the clocks change lands on
 * the right side of the change.
 *
 * @param date `yyyy-MM-dd`.
 * @param timeZone An IANA zone, such as the group's `timeZoneId`.
 * @returns null when the date does not parse.
 */
export function startOfDayUtc(date: string, timeZone: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  if (!match) return null

  const [, y, m, d] = match.map(Number) as [number, number, number, number]
  const wall = Date.UTC(y, m - 1, d)

  let guess = wall
  for (let i = 0; i < 2; i++) {
    guess = wall - offsetAt(guess, timeZone)
  }

  return new Date(guess).toISOString()
}

/** How far ahead of UTC a zone's wall clock is at an instant, in milliseconds. */
function offsetAt(instant: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(instant))

  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value)

  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  )

  return asUtc - (instant - (instant % 1000))
}

/**
 * The day after a `yyyy-MM-dd` date, as `yyyy-MM-dd`.
 *
 * An "until" date is inclusive to the person picking it and exclusive to the
 * query: until 30 September means before the start of 1 October.
 */
export function nextDay(date: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10)
}

/**
 * An instant as a local date and time in a zone: `{ date: 'yyyy-MM-dd', time: 'HH:mm' }`.
 *
 * For putting a stored instant back into a form — a meal's reply deadline in
 * the meal's own zone — so saving the form does not quietly move it.
 */
export function localParts(iso: string, timeZone: string): { date: string; time: string } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date(iso))

  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''

  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    time: `${get('hour')}:${get('minute')}`,
  }
}
